<template>
  <OmtLayoutContainer
    bg="#F6F6F9"
    :minHeight="['initial', 'calc(100vh - 80px)']"
  >
    <OmtRow v-if="avaliacao">
      <CBox
        width="100%"
        mt="16px"
        pb="24px"
      >
        <!-- Cabecalho: imagem + identificacao + resumo da analise -->
        <div :class="['topo', { 'topo--sem-imagem': !imagemUrl }]">
          <CBox
            v-if="imagemUrl"
            as="button"
            type="button"
            class="topo__imagem"
            position="relative"
            p="0"
            bg="transparent"
            border="none"
            cursor="pointer"
            aria-label="Ampliar imagem enviada para análise"
            @click="imagemAmpliada = true"
          >
            <CImage
              :src="imagemUrl"
              alt="Imagem enviada para análise"
              width="100%"
              height="100%"
              objectFit="cover"
              borderRadius="8px"
            />
            <CFlex
              position="absolute"
              right="6px"
              bottom="6px"
              width="26px"
              height="26px"
              borderRadius="50%"
              bg="rgba(255, 255, 255, 0.9)"
              alignItems="center"
              justifyContent="center"
            >
              <OmtIcons
                name="expand"
                size="xs"
                color="var(--colors-light-primary-navy-blue)"
              />
            </CFlex>
          </CBox>
          <CBox class="topo__info">
            <OmtTypography
              as="h1-bold"
              color="var(--colors-light-primary-dark-blue)"
              mb="4px"
            >
              Análise de pele
            </OmtTypography>
            <OmtTypography
              as="p2"
              color="var(--colors-light-primary-gray2)"
            >
              Protocolo {{ avaliacao.ProtocoloANS }}
            </OmtTypography>
            <OmtTypography
              as="p2"
              color="var(--colors-light-primary-gray2)"
              mb="8px"
            >
              Solicitada em {{ avaliacao.DataInclusao }}
            </OmtTypography>
            <StatusTile
              v-chakra
              :label="avaliacao.Status"
              :color="statusColor"
            />
          </CBox>
          <OmtTypography
            v-if="resumo"
            as="p1"
            color="var(--colors-light-primary-gray1)"
            class="topo__resumo texto-relatorio"
          >
            {{ resumo }}
          </OmtTypography>
        </div>

        <!-- Em analise: o resultado so aparece com o relatorio final -->
        <CFlex
          v-if="pendente"
          bg="#FFFFFF"
          border="1px solid #DADADA"
          borderRadius="8px"
          p="20px"
          alignItems="flex-start"
        >
          <OmtIcons
            name="clock"
            size="lg"
            color="var(--colors-light-primary-navy-blue)"
            mr="12px"
            mt="2px"
          />
          <CBox>
            <OmtTypography
              as="h2-bold"
              color="var(--colors-light-primary-navy-blue)"
              mb="8px"
            >
              Em análise
            </OmtTypography>
            <OmtTypography
              as="p1"
              color="#4E4E4E"
            >
              O resultado da sua análise será disponibilizado em breve.
            </OmtTypography>
          </CBox>
        </CFlex>

        <SkiniverReport
          v-else-if="relatorio"
          :relatorio="relatorio"
        />

        <OmtTypography
          v-else
          as="p1"
          color="var(--colors-light-primary-gray2)"
          mb="16px"
        >
          O resultado desta análise ainda não está disponível.
        </OmtTypography>

        <!-- Recados da equipe e, por ultimo, proximos passos: so com a analise concluida -->
        <div
          v-if="!pendente"
          class="linha-final"
        >
          <SkiniverRecados
            v-if="recados.length"
            :recados="recados"
          />

          <CBox
            bg="#FFFFFF"
            border="1px solid #DADADA"
            borderRadius="8px"
            p="20px"
          >
            <CFlex
              alignItems="center"
              mb="8px"
            >
              <OmtIcons
                name="user-doctor"
                size="lg"
                color="var(--colors-light-primary-navy-blue)"
                mr="8px"
              />
              <OmtTypography
                as="h2-bold"
                color="var(--colors-light-primary-navy-blue)"
              >
                Próximos passos
              </OmtTypography>
            </CFlex>
            <OmtTypography
              as="p1"
              color="#4E4E4E"
              mb="16px"
            >
              {{ textoProximosPassos }}
            </OmtTypography>
            <div class="acoes">
              <OmtButton
                size="md"
                px="24px"
                borderRadius="95px"
                @click="handleBuscarDermatologista"
              >
                Buscar dermatologista
              </OmtButton>

              <OmtButton
                v-if="possuiDrOmint"
                variant="outline"
                size="md"
                px="24px"
                borderRadius="95px"
                @click="handleDrOmint"
              >
                Consultar com Dr. Omint
              </OmtButton>
            </div>
          </CBox>
        </div>

        <!-- Aviso / Disclaimer -->
        <CFlex
          v-if="aviso"
          alignItems="flex-start"
        >
          <OmtIcons
            name="circle-info"
            color="var(--colors-light-primary-gray2)"
            mr="8px"
            mt="2px"
          />
          <OmtTypography
            as="p2"
            color="var(--colors-light-primary-gray2)"
          >
            {{ aviso }}
          </OmtTypography>
        </CFlex>
      </CBox>

      <!-- Imagem ampliada -->
      <CModal
        :isOpen="imagemAmpliada"
        :onClose="() => (imagemAmpliada = false)"
        isCentered
        size="xl"
      >
        <CModalContent
          bg="transparent"
          boxShadow="none"
          mx="16px"
        >
          <CModalCloseButton
            bg="#FFFFFF"
            borderRadius="50%"
          />
          <CModalBody p="0">
            <CImage
              :src="imagemUrl"
              alt="Imagem enviada para análise"
              width="100%"
              borderRadius="8px"
            />
          </CModalBody>
        </CModalContent>
        <CModalOverlay />
      </CModal>
    </OmtRow>

    <OmtRow v-else-if="error">
      <OmtTypography
        as="p1"
        color="var(--colors-light-primary-gray2)"
        mt="16px"
      >
        {{ error }}
      </OmtTypography>
    </OmtRow>
  </OmtLayoutContainer>
</template>

<script>
import { mapActions, mapState } from "vuex";
import {
  CFlex,
  CBox,
  CImage,
  CModal,
  CModalOverlay,
  CModalContent,
  CModalBody,
  CModalCloseButton
} from "@chakra-ui/vue";
import StatusTile from "@/components/Organisms/Requests/StatusTile";
import SkiniverReport from "@/components/Organisms/Skiniver/SkiniverReport.vue";
import SkiniverRecados from "@/components/Organisms/Skiniver/SkiniverRecados.vue";

// CodigoStatus da analise ja com o relatorio final.
const CODIGO_STATUS_CONCLUIDA = "C";

// Rota liberada pelo menu "Dr. Omint".
const DR_OMINT_ROUTE = "/redireciona-dr-omint";

export default {
  name: "SkiniverDetail",
  components: {
    CFlex,
    CBox,
    CImage,
    CModal,
    CModalOverlay,
    CModalContent,
    CModalBody,
    CModalCloseButton,
    StatusTile,
    SkiniverReport,
    SkiniverRecados
  },
  props: {
    id: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      error: null,
      imagemAmpliada: false
    };
  },
  computed: {
    ...mapState("skiniver", ["chamados"]),
    ...mapState("saude", ["menu", "menusFavoritos"]),
    possuiDrOmint() {
      // Mesma regra do guard de rotas (hasAccessPermition): o menu ou um favorito aponta para a rota.
      return [...(this.menu || []), ...(this.menusFavoritos || [])].some(m => m.View?.startsWith(DR_OMINT_ROUTE));
    },
    avaliacao() {
      return (this.chamados || []).find(a => String(a.Id) === this.id) || null;
    },
    pendente() {
      return this.avaliacao?.CodigoStatus !== CODIGO_STATUS_CONCLUIDA;
    },
    imagemUrl() {
      // A imagem fica guardada pela Skinive; e a url que vem no proprio retorno da analise.
      return this.avaliacao?.Analise?.image_url || "";
    },
    relatorio() {
      // RetornoFinal guarda a decisao da Plataforma Wellness inteira; o conteudo fica em "report".
      if (!this.avaliacao?.RetornoFinal) return null;
      try {
        return JSON.parse(this.avaliacao.RetornoFinal)?.report || null;
      } catch (error) {
        console.error(error);
        return null;
      }
    },
    resumo() {
      return this.relatorio?.summary || "";
    },
    recados() {
      return (this.relatorio?.reviewerNotes || []).filter(Boolean);
    },
    aviso() {
      return this.relatorio?.disclaimer || "";
    },
    textoProximosPassos() {
      // O texto acompanha os botoes que aparecem: o do Dr. Omint depende do menu.
      return this.possuiDrOmint
        ? "Quer buscar um dermatologista na sua rede credenciada ou agendar uma consulta on-line com o Dr. Omint?"
        : "Quer buscar um dermatologista na sua rede credenciada?";
    },
    statusColor() {
      return !this.pendente ? "var(--colors-light-support-success)" : "var(--colors-light-support-warning)";
    }
  },
  async mounted() {
    if (this.avaliacao) {
      this.$root.$emit("hide-loading");
      return;
    }

    this.$root.$emit("show-loading");
    try {
      await this.listChamados();
      if (!this.avaliacao) {
        this.error = "Análise não encontrada";
      }
    } catch (error) {
      console.error(error);
      this.error = "Serviço temporariamente indisponível";
    } finally {
      this.$root.$emit("hide-loading");
    }
  },
  methods: {
    ...mapActions("skiniver", ["listChamados"]),
    handleBuscarDermatologista() {
      this.$router.push({
        name: "redecredenciada-prototipo.chat",
        query: { pergunta: "Dermatologista perto de mim" }
      });
    },
    handleDrOmint() {
      this.$router.push({ path: DR_OMINT_ROUTE });
    }
  }
};
</script>

<style scoped>
/* Card do topo: no mobile, imagem sozinha na primeira linha (largura toda) e identificacao + resumo embaixo;
   no desktop, imagem a esquerda e identificacao + resumo a direita. */
.topo {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  grid-template-areas:
    "imagem"
    "info"
    "resumo";
  column-gap: 16px;
  row-gap: 16px;
  background: #ffffff;
  border: 1px solid #dadada;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
}

.topo--sem-imagem {
  grid-template-columns: minmax(0, 1fr);
  grid-template-areas:
    "info"
    "resumo";
}

.topo__imagem {
  grid-area: imagem;
  width: 100%;
  height: 200px;
}

.topo__info {
  grid-area: info;
  min-width: 0;
}

/* Separacao via row-gap do grid: o reset global zera margin-top de <p> com !important. */
.topo__resumo {
  grid-area: resumo;
  padding-top: 16px;
  border-top: 1px solid #ededed;
}

@media screen and (min-width: 1024px) {
  .topo {
    grid-template-columns: 200px minmax(0, 1fr);
    grid-template-rows: auto 1fr;
    grid-template-areas:
      "imagem info"
      "imagem resumo";
    column-gap: 24px;
    padding: 20px;
  }

  .topo--sem-imagem {
    grid-template-columns: minmax(0, 1fr);
    grid-template-areas:
      "info"
      "resumo";
  }

  .topo__imagem {
    width: 200px;
    height: 200px;
  }
}

/* O resumo pode vir com quebras de linha (ex.: texto editado pela equipe). */
.texto-relatorio {
  white-space: pre-line;
}

/* Recados e proximos passos: um card por linha em qualquer resolucao. */
.linha-final {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 16px;
}

/* Botoes lado a lado, so com a largura do texto; quebram linha quando nao cabem. */
.acoes {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}
</style>
